/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import ChevronUp from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { ERROR_LOCATION__RIBBON, NavErrorBoundary } from '@atlassian/navigation-error-boundary';

import { useRibbonConfig } from '../../../controllers/ribbon-config-provider/hooks';

import { ErrorState } from './error-state';
import type { OrgSwitcherOrgProps, OrgSwitcherProps } from './types';
import UnitSwitcher from './unit-switcher';

const styles = cssMap({
	switcherTrigger: {
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
			textDecoration: 'none',
		},
		width: '100%',
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.overlay'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '312px', // TODO https://hello.jira.atlassian.cloud/browse/NAVX-3824 : Determine if this is fixed or dynamic (should match width of sidenav?)
	},
	orgListContainer: {
		overflow: 'auto',
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
	},
	chevronIcon: {
		marginTop: token('space.100'),
		marginRight: token('space.100'),
		marginBottom: token('space.100'),
		marginLeft: token('space.100'),
	},
});

const OrgSwitcherComponent = (props: OrgSwitcherProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedOrg, setSelectedOrg] = useState<OrgSwitcherOrgProps>(() => {
		return props.orgs.find((org) => org.id === props.currentOrgId) ?? props.orgs[0];
	});

	const [selectedUnit, setSelectedUnit] = useState<string>(props.currentUnitId);

	const onOrgSelected = (org: OrgSwitcherOrgProps) => {
		setSelectedOrg(org);
		setIsOpen(false);
	};

	return (
		<Stack xcss={styles.container}>
			{/* Org switcher part -> Swap between orgs */}
			<DropdownMenu
				shouldFitContainer
				shouldFlip={false}
				isOpen={isOpen}
				shouldRenderToParent
				onOpenChange={(attrs) => {
					setIsOpen(attrs.isOpen);
				}}
				trigger={({ triggerRef, onClick, ...triggerProps }) => (
					<Pressable
						{...triggerProps}
						ref={triggerRef}
						xcss={styles.switcherTrigger}
						isDisabled={props.orgs.length <= 1}
						onClick={(e) => {
							onClick?.(e);
						}}
					>
						<Inline alignBlock="center" space="space.100" spread="space-between">
							<Inline alignBlock="center" space="space.100">
								{/* TODO https://hello.jira.atlassian.cloud/browse/NAVX-3824 : Implement styling for the org tile once designs are finalized */}
								<Tile label={selectedOrg.name} />
								<Text align="start">{selectedOrg.name}</Text>
							</Inline>
							{/* Show chevron only if there's more than 1 org */}
							{props.orgs.length > 1 ? (
								isOpen ? (
									<Box xcss={styles.chevronIcon}>
										<ChevronUp label="Collapse" size="small" />
									</Box>
								) : (
									<Box xcss={styles.chevronIcon}>
										<ChevronDown label="Expand" size="small" />
									</Box>
								)
							) : null}
						</Inline>
					</Pressable>
				)}
			>
				{/* TODO https://hello.jira.atlassian.cloud/browse/NAVX-3824 : Implement styling for the org list once designs are finalized */}
				<Box xcss={styles.orgListContainer}>
					<DropdownItemGroup>
						{props.orgs.map((org) => (
							<DropdownItem
								key={org.name}
								isSelected={org.name === selectedOrg.name}
								onClick={() => onOrgSelected(org)}
							>
								{org.name}
							</DropdownItem>
						))}
					</DropdownItemGroup>
				</Box>
			</DropdownMenu>

			{/* Unit switcher part -> Swap between units/sites */}
			<UnitSwitcher
				orgName={selectedOrg.name}
				units={selectedOrg.units}
				setSelectedUnit={(unitId) => setSelectedUnit(unitId)}
				selectedUnitId={selectedUnit}
			/>
		</Stack>
	);
};

export default function OrgSwitcher(props: OrgSwitcherProps): JSX.Element {
	const { product } = useRibbonConfig();

	return (
		<NavErrorBoundary
			location={`${ERROR_LOCATION__RIBBON}--org-switcher`}
			sentryConfig={{ product }}
			errorFallbackUI={() => (
				<Stack xcss={styles.container}>
					<ErrorState />
				</Stack>
			)}
		>
			<OrgSwitcherComponent {...props} />
		</NavErrorBoundary>
	);
}
